import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

// Shadcn Components
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";

// Icons
import { ArrowLeft } from "lucide-react";

// Hooks
import { useCreateOrganization } from '@/hooks/authentication/useCreateOrganization';
import { useOrganizationOnboardingFlag } from '@/hooks/common/useFeatureFlag';

const CreateOrganization = () => {
  const navigate = useNavigate();
  const {
    formData,
    error,
    passwordError,
    handleChange,
    handleSubmit
  } = useCreateOrganization();

  const { isEnabled: organizationOnboardingEnabled, loading } = useOrganizationOnboardingFlag();

  // Redirect to auth choice if organization onboarding is disabled
  useEffect(() => {
    if (!loading && !organizationOnboardingEnabled) {
      navigate('/auth');
    }
  }, [organizationOnboardingEnabled, loading, navigate]);

  // Show loading or redirect if feature is disabled
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!organizationOnboardingEnabled) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Redirecting...</p>
        </div>
      </div>
    );
  }

  const gradientStyle = {
    background: "linear-gradient(45deg, #f804b4 0%, #08ccc4 100%)"
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4 py-12" 
      style={gradientStyle}
    >
      <div className="flex flex-col w-[90%] sm:w-[85%] md:w-[75%] lg:w-[65%] xl:w-[55%] max-w-3xl rounded-lg overflow-hidden bg-white shadow-2xl">
        <div className="w-full bg-white p-2 px-6">
          <div className="w-full max-w-[600px] mx-auto flex flex-col h-[calc(100vh-160px)]">
            <div className="flex flex-col items-center relative mb-2">
              <div className="bg-white p-4 pb-0 rounded-lg">
                <img 
                  src="/branding/TriEvents_Loading_Big_Text.gif" 
                  alt="TriEvents Logo" 
                  className="w-40 sm:w-48 h-auto"
                />
              </div>
              <h1 className="text-xl sm:text-2xl font-semibold text-foreground text-center">
                Create Your Organization
              </h1>
              <p className="text-muted-foreground text-center text-sm max-w-[500px] font-medium mt-0.5 mb-4">
                Fill in your information and organization details to get started
              </p>
            </div>

            <div className="flex-1 overflow-y-auto rounded-lg pt-6 border bg-gray-50 scrollbar-thin scrollbar-track-muted/20 scrollbar-thumb-muted/40 hover:scrollbar-thumb-muted/60">
              <form id="create-org-form" onSubmit={handleSubmit} className="w-full mx-auto px-3">
                {/* User Information Section */}
                <Card className="mb-6">
                  <CardContent className="pt-6">
                    <h2 className="text-lg font-semibold text-foreground mb-4">User Information</h2>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Name</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="password">Password</Label>
                          <Input
                            type="password"
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="confirmPassword">Confirm Password</Label>
                          <Input
                            type="password"
                            id="confirmPassword"
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            required
                          />
                          {passwordError && (
                            <p className="text-destructive text-sm">{passwordError}</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Password Requirements */}
                      <div className="text-xs text-muted-foreground space-y-1">
                        <p className="font-medium">Password Requirements:</p>
                        <ul className="list-disc list-inside space-y-0.5">
                          <li>At least 8 characters long</li>
                          <li>At least one lowercase letter (a-z)</li>
                          <li>At least one uppercase letter (A-Z)</li>
                          <li>At least one number (0-9)</li>
                          <li>At least one special character (!@#$%^&*(),.?":{}|&lt;&gt;)</li>
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Organization Information Section */}
                <Card className="mb-6">
                  <CardContent className="pt-6">
                    <h2 className="text-lg font-semibold text-foreground mb-4">Organization Information</h2>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="orgName">Organization Name</Label>
                          <Input
                            id="orgName"
                            name="orgName"
                            value={formData.orgName}
                            onChange={handleChange}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="orgId">Organization ID</Label>
                          <Input
                            id="orgId"
                            name="orgId"
                            value={formData.orgId}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="orgDescription">Organization Description</Label>
                        <Textarea
                          id="orgDescription"
                          name="orgDescription"
                          value={formData.orgDescription}
                          onChange={handleChange}
                          required
                          className="h-24 resize-none"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {error && (
                  <Alert variant="destructive" className="mb-4 bg-white">
                    <AlertDescription>
                      {error}
                    </AlertDescription>
                  </Alert>
                )}
              </form>
            </div>

            <div className="sticky bottom-0 left-0 right-0 bg-white py-4">
              <div className="flex flex-col-reverse sm:flex-row items-center justify-between gap-4">
                <Button
                  variant="outline"
                  onClick={() => navigate('/auth')}
                  className="transform hover:scale-105 transition-all duration-200 w-full sm:w-auto"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
                <Button 
                  type="submit"
                  form="create-org-form"
                  size="lg"
                  className="w-full sm:w-[280px] transition-transform hover:scale-105"
                >
                  Continue
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateOrganization;
